import { useCallback, useContext, useEffect, useRef, useState } from "react";
import { useHistory, useLocation } from "react-router";
import { singletonHook } from "react-singleton-hook";
import {
  IBreadcrumbItem,
  INavLink,
  INavLinkGroup,
  ResponsiveMode,
  useResponsiveMode,
} from "@fluentui/react";
import { INavLink as IAdminNavLink } from "@cayo/shared/api/types/schema";
import { AppContext } from "@cayo/shared/components/App/appContext";
import { appEvents } from "@cayo/shared/components/App/appEvents";
import ajax from "@cayo/shared/libs/ajax";
import logger from "@cayo/shared/libs/logger";
import { endpoints } from "@cayo/shared/services/endpoints.service";
import useService from "@cayo/shared/services/hooks/useService";
import { ajaxUtils } from "@cayo/shared/utils/ajax";
import customUrlUtils from "@cayo/shared/utils/customUrlUtils";
import { NavController, toNodeId } from "./nav-controller";
import { showLazyProps } from "../../utils/lazy-props";
import useTypeAnnotations from "../GlobalHooks/type-annotations.hook";
import { useUserSettings } from "../GlobalHooks/userSettingsHook";

const log = logger.getLogger("nav-hooks");

const ctrl = new NavController(endpoints.isAdminMode);

export const isMedium = (mode: ResponsiveMode): boolean => {
  return (mode as number) <= ResponsiveMode.medium;
};

function findRootNode(nodes: IAdminNavLink[]): IAdminNavLink | null {
  if (!Array.isArray(nodes) || nodes.length === 0) {
    return null;
  }

  const search = (arr: IAdminNavLink[]): IAdminNavLink | null => {
    for (const node of arr) {
      if (node.isRoot) {
        return node;
      }

      if (Array.isArray(node.links) && node.links.length > 0) {
        const found = search(node.links);
        if (found) {
          return found;
        }
      }
    }

    return null;
  };

  const rootNode = search(nodes);

  if (rootNode) {
    return rootNode;
  }

  const first = nodes[0];
  if (Array.isArray(first.links) && first.links.length > 0) {
    return first.links[0];
  }

  return first;
}

function handleSearchProps(location: { pathname: string; search: string }, nodePath: string) {
  if (location.search.toLowerCase().indexOf("?props=") !== -1) {
    let actionUrl = customUrlUtils.getActionUrl(location.pathname + location.search);
    if (location.search.toLowerCase().indexOf("&action=") !== -1) {
      const actionPath = location.search.split("&action=")[1];
      actionUrl += `/${actionPath}`;

      appEvents.trigger({
        action: {
          schemeId: actionUrl!,
          gridId: "dummy",
        },
      });
      return;
    }

    if (nodePath === "/") {
      appEvents.trigger({
        showControl: { controlId: "scheme", parameters: actionUrl },
      });
    } else {
      showLazyProps(nodePath, actionUrl!);
    }
  }
}

export function useNav(rootLinks: IAdminNavLink[]) {
  const location = useLocation();
  const history = useHistory();
  const { collapsed, toggleCollapsed } = useToggleNavCollapsed();
  const navTreeClient = useService("navTreeClient");
  const [isLoading, setIsLoading] = useState(true);
  const { showServerUnavailable } = useContext(AppContext);

  const initialized = useRef(false);
  const stubRef = useRef(null);
  const mediumMode = isMedium(useResponsiveMode(stubRef));

  const [groups, setGroups] = useState<INavLinkGroup[]>([]);
  const { getTypeAnnotation } = useTypeAnnotations();
  const getIcon = (oType: string) =>
    getTypeAnnotation(oType)?.icon?.iconId ?? "CheckboxIndeterminate";

  const { fetchFn } = ajax;
  const [selectedKey, setSelectedKey] = useState("");

  useEffect(() => {
    log.debug("useNav.OnMount -> rootLinks, location, mediumMode", rootLinks, location, mediumMode);

    setIsLoading(true);
    // set collapsed by responsiveMode on initial
    if ((mediumMode && !collapsed) || (!mediumMode && collapsed)) {
      toggleCollapsed();
    }

    //   .then((nodes) => {
    //     log.debug("useNav -> loaded root nodes", nodes);
    //     return ctrl.setRootNodes(nodes);
    //   })
    navTreeClient
      .getNavTree()
      .then((groups) => {
        // init node tree

        const rootNode = groups ? findRootNode(groups) : null;

        if (groups && location.pathname === "/" && rootNode) {
          const newPath = `/${rootNode.path}`;
          history.push(newPath);
          location.pathname = newPath;
        }

        ctrl.setRootNodes(groups);
        return ctrl.loadRootSubnodes(fetchFn, getIcon);
      })
      .then(() => {
        return ctrl.handleLocation(location, fetchFn, getIcon);
      })
      .then((result) => {
        log.debug("handleLocation -> result", result);
        setGroups(ctrl.getNavGroups());
        setSelectedKey(result.selectedKey);
        if (result.location !== location) {
          history.push(result.location.pathname);
        }
        initialized.current = true;
        handleSearchProps(location, result.location.pathname);
      })
      .catch(async (e) => {
        const error = await ajaxUtils.getError(e);
        showServerUnavailable({
          state: "failed",
          isCritical: true,
          message: error.error_description,
        });
      })
      .finally(() => setIsLoading(false));
  }, [mediumMode]);

  useEffect(() => {
    if (!initialized.current) {
      return;
    }

    ctrl.handleLocation(location, fetchFn, getIcon).then((result) => {
      if (result.location.pathname !== location.pathname) {
        history.push(result.location.pathname);
      } else {
        setSelectedKey(result.selectedKey);
        if (result.needRerender) {
          setGroups(ctrl.getNavGroups());
        }
      }
      handleSearchProps(location, result.location.pathname);
    });
  }, [location.pathname]);

  return {
    groups,
    selectedKey,
    isMedium: mediumMode,
    isLoading,
    setSelectedKey,
    onLinkExpandHandler: async (ev?: React.MouseEvent<HTMLElement>, link?: INavLink) => {
      log.debug("onLinkExpandHandler -> link", link);
      if (!link) {
        return;
      }
      const targetNode = ctrl.getById(link.key!);
      if (!targetNode) {
        return;
      }
      targetNode.expanded = !link.isExpanded;
      const needUpdate = await ctrl.loadSubNodes(targetNode!, fetchFn, getIcon);
      if (needUpdate) {
        setGroups(ctrl.getNavGroups());
      }
    },
    resolveName: ctrl.getFullName,
  };
}

function loadBreadcrumbs(path: string, rootName?: string): IBreadcrumbItem[] {
  log.debug("loadBreadcrumbs -> path, productName", path, rootName);
  return ctrl.makeBreadcrumbs(path, rootName);
}

export function useBreadcrumbs(rootName?: string): IBreadcrumbItem[] {
  const [breadcrumbItems, setBreadcrumbItems] = useState<IBreadcrumbItem[]>([]);

  useEffect(() => {
    const evtHandler = {
      handleEvent: (evt: CustomEvent<string>) => {
        const breadCrumbs = loadBreadcrumbs(evt.detail, rootName);
        log.debug("breadCrumbs", breadCrumbs);
        log.debug("useBreadcrumbs.handleEvent", evt.detail);
        setBreadcrumbItems(breadCrumbs);
      },
    };

    ctrl.addEventListener(NavController.locationChangedEvent, evtHandler);
    return () => ctrl.removeEventListener(NavController.locationChangedEvent, evtHandler);
  }, []);

  return breadcrumbItems;
}

export function resolveNodePath(pathname: string) {
  const nodeId = toNodeId(pathname);

  const indexedNode = ctrl.getById(nodeId);

  if (indexedNode) {
    return indexedNode.path ?? "";
  }

  return ctrl.getByPath(nodeId)?.path ?? "";
}

export function useNavSelectedSchemeId(): string {
  const { pathname } = useLocation();
  const [schemeId, setSchemeId] = useState("");
  const evtHandler = useRef<EventListenerObject>({
    handleEvent: (evt: CustomEvent<string>) => {
      const path = resolveNodePath(evt.detail);
      setSchemeId(path);
    },
  }).current;

  useEffect(() => {
    setSchemeId(resolveNodePath(pathname));
    ctrl.addEventListener(NavController.locationChangedEvent, evtHandler);
    return () => ctrl.removeEventListener(NavController.locationChangedEvent, evtHandler);
  }, []);

  return schemeId;
}

export const useToggleNavCollapsed = singletonHook(
  { collapsed: false, toggleCollapsed: () => {} },
  () => {
    const { userSettings, setUserSettings } = useUserSettings();
    const collapsed = !!userSettings?.isSidebarCollapsed;

    const toggleCollapsed = useCallback(() => {
      setUserSettings({ isSidebarCollapsed: !collapsed });
    }, [collapsed, setUserSettings]);

    return { collapsed, toggleCollapsed };
  }
);
